import {
  DownOutlined,
  InfoCircleOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { t } from '@hefe/i18n';
import { UserCustomAttributeType, UserImporterTableColumn } from '@hefe/models';
import { UsersImporterSlice, UsersSlice } from '@hefe/store';
import { Button, Col, Row, Space, Tooltip, Transfer, Typography } from 'antd';
import { Key } from 'antd/es/table/interface';
import { TransferDirection, TransferListProps } from 'antd/es/transfer';
import { arrayMoveImmutable } from 'array-move';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../../store/store';

export default function UsersImportColumnSelectorComponent() {
  const dispatch = useAppDispatch();
  const headers = useSelector(UsersImporterSlice.usersImporterHeadersSelector);
  const customAttributes = useSelector(
    UsersSlice.usersCustomUserAttributesSelector
  );

  const [columns, setColumns] = useState<UserImporterTableColumn[]>([]);
  const [targetKeys, setTargetKeys] = useState<Key[]>([]);
  const [selectedTargetKeys, setSelectedTargetKeys] = useState<Key[]>([]);

  const defaultColumns: UserImporterTableColumn[] = [
    {
      key: 'firstname',
      title: t('firstname'),
      required: true,
      type: UserCustomAttributeType.STRING,
    },
    {
      key: 'lastname',
      title: t('lastname'),
      required: true,
      type: UserCustomAttributeType.STRING,
    },
    {
      key: 'email',
      title: t('email'),
      required: false,
      type: UserCustomAttributeType.MAIL,
      nullable: true,
    },
    {
      key: 'birthday',
      title: t('birthday'),
      required: false,
      type: UserCustomAttributeType.DATE,
    },
    {
      key: 'street',
      title: `${t('address')}: ${t('street')}`,
      required: false,
      type: UserCustomAttributeType.STRING,
    },
    {
      key: 'zipcode',
      title: `${t('address')}: ${t('zipcode')}`,
      required: false,
      type: UserCustomAttributeType.NUMBER,
    },
    {
      key: 'city',
      title: `${t('address')}: ${t('city')}`,
      required: false,
      type: UserCustomAttributeType.STRING,
    },
    {
      key: 'country',
      title: `${t('address')}: ${t('country')}`,
      required: false,
      type: UserCustomAttributeType.STRING,
    },
    {
      key: 'phone',
      title: t('phone'),
      required: false,
      type: UserCustomAttributeType.STRING,
    },
    {
      key: 'sex',
      title: t('sex.title'),
      required: false,
      type: UserCustomAttributeType.SEX,
    },
    {
      key: 'nationality',
      title: t('nationality'),
      required: false,
      type: UserCustomAttributeType.STRING,
    },
    {
      key: 'represented_by',
      title: t('represented_by'),
      required: false,
      type: UserCustomAttributeType.STRING,
      nullable: true,
    },
    {
      key: 'linked_accounts',
      title: t('linked_accounts'),
      hint: t('information_texts.linked_accounts'),
      required: false,
      type: UserCustomAttributeType.MULTI_MAIL,
      nullable: true,
    },
    {
      key: 'tan',
      title: t('tan'),
      required: false,
      type: UserCustomAttributeType.NUMBER,
      basketball: true,
      nullable: true,
    },
  ];

  useEffect(() => {
    addCustomColumns();
  }, [customAttributes]);

  useEffect(() => {
    let targetKeys = defaultColumns
      .filter((col) => col.required)
      .map((col) => col.key);

    if (headers.length > 0) {
      targetKeys = headers.map((col) => col.key);
    }

    setColumns(defaultColumns);
    setTargetKeys(targetKeys);
    setSelectedTargetKeys([]);
    addCustomColumns();
  }, []);

  useEffect(() => {
    const headers: UserImporterTableColumn[] = [];
    targetKeys.forEach((key) => {
      const column = columns.find((col) => col.key === key);
      if (column) {
        headers.push(column);
      }
    });
    dispatch(UsersImporterSlice.usersImporterActions.setHeaders(headers));
  }, [targetKeys]);

  const addCustomColumns = () => {
    const customColumns: UserImporterTableColumn[] = [];
    customAttributes.forEach((att) => {
      customColumns.push({
        key: 'custom_input_' + att.id,
        title: att.name,
        type: att.type,
        required: false,
      });
    });

    setColumns((columns) => [...columns, ...customColumns]);
  };

  const sortTargetKeys = (up: boolean) => {
    const currentIndex = targetKeys.indexOf(selectedTargetKeys[0]);
    const currentTargetKeys = [...targetKeys];

    if (
      (up && currentIndex > 0) ||
      (!up && currentIndex < targetKeys.length - 1)
    ) {
      const newIndex = up ? currentIndex - 1 : currentIndex + 1;
      const newTargetKeys = arrayMoveImmutable(
        currentTargetKeys,
        currentIndex,
        newIndex
      );

      setTargetKeys(newTargetKeys);
    }
  };

  const handleChange = (
    nextTargetKeys: Key[],
    direction: TransferDirection,
    moveKeys: any[]
  ) => {
    let allowed = true;
    moveKeys.forEach((key) => {
      const column = defaultColumns.find(
        (col: UserImporterTableColumn) => col.key === key
      );
      if (column && column.required) {
        allowed = false;
      }
    });

    if (allowed) {
      if (direction === 'right') {
        setTargetKeys([...targetKeys, ...moveKeys]);
      } else {
        const newTargetKeys = targetKeys.filter(
          (key) => !moveKeys.includes(key)
        );
        setTargetKeys(newTargetKeys);
      }
    } else {
      alert('Elemente mit Stern (*) Markierung müssen vorhanden sein!');
    }
  };

  const handleSelectChange = (
    sourceSelectedKeys: Key[],
    targetSelectedKeys: Key[]
  ) => {
    setSelectedTargetKeys(targetSelectedKeys);
  };

  const renderFooter = (element: TransferListProps<any>) => {
    if (element.direction === 'right') {
      const disabled = selectedTargetKeys.length !== 1;
      return (
        <div style={{ float: 'right', margin: 5 }}>
          <Space>
            <Button
              disabled={disabled}
              size="small"
              icon={<UpOutlined />}
              onClick={() => sortTargetKeys(true)}
            />
            <Button
              disabled={disabled}
              size="small"
              onClick={() => sortTargetKeys(false)}
              icon={<DownOutlined />}
            />
          </Space>
        </div>
      );
    }

    return null;
  };

  return (
    <Row>
      <Col xs={24}>
        <Transfer
          dataSource={columns}
          titles={['Verfügbar', 'Ausgewählt']}
          targetKeys={targetKeys}
          showSearch
          listStyle={{
            width: '100%',
            height: '70%',
          }}
          onChange={handleChange}
          onSelectChange={handleSelectChange}
          render={(item) => {
            const tooltip = item.hint ? (
              <Tooltip title={item.hint}>
                <InfoCircleOutlined />
              </Tooltip>
            ) : undefined;
            return (
              <Typography.Text>
                {`${item.title} ${item.required ? '*' : ''}`}
                {tooltip}
              </Typography.Text>
            );
          }}
          footer={renderFooter}
        />
      </Col>
      <Col xs={24}>
        <Row justify="end">
          <Space>
            <Button
              onClick={() =>
                dispatch(
                  UsersImporterSlice.usersImporterActions.decrementStep()
                )
              }
            >
              {t('prev')}
            </Button>
            <Button
              onClick={() =>
                dispatch(
                  UsersImporterSlice.usersImporterActions.incrementStep(4)
                )
              }
            >
              {t('next')}
            </Button>
          </Space>
        </Row>
      </Col>
    </Row>
  );
}
